<script>
import { mapActions } from 'vuex'

export default {
  name: 'ArticlesList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    FilterList: () => import('@/components/general/FilterList'),
    Pagination: () => import('@/components/general/Pagination'),
    CheckboxItem: () => import('@/components/general/CheckboxItem'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch')
  },

  data () {
    return {
      pagination: {
        page: 1,
        limit: 7,
        lastPage: null,
        query: {},
        order: { created_at: 'desc' }
      },

      modalRemoveOpen: false,
      articleId: null,
      featuredVersion: 0
    }
  },

  computed: {
    articlesList () {
      return this.$store.getters.articlesList
    },

    canWrite () {
      return this.getContextPermission('articles') === 'write'
    }
  },

  watch: {
    'pagination.page' () {
      this.getArticleList()
    }
  },

  created () {
    this.getArticleList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetArticlesList',
      'attemptToggleFeaturedArticle',
      'attemptPublishArticle',
      'attemptHideArticle',
      'attemptRemoveArticle'
    ]),

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    getArticleList () {
      this.setFetching(true)

      this.attemptGetArticlesList(this.pagination)
        .then(response => {
          this.pagination.lastPage = response.lastPage
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    searchArticle (search) {
      this.pagination.page = 1
      this.pagination.query = search ? { title: search } : {}
      this.getArticleList()
    },

    createNewArticle () {
      this.$router.push({ name: 'articles.manage' })
    },

    edit (idArticle) {
      this.$router.push({
        name: 'articles.manage',
        params: { id: idArticle }
      })
    },

    toggleFeatured (item) {
      this.setFetching(true)

      this.attemptToggleFeaturedArticle(item.id)
        .then(() => {
          this.setFeedback({ message: this.$t('articles.feedback:toggle.featured.success') })
          this.getArticleList()
        })
        .catch(err => {
          const message = err?.data?.error?.message || err?.data?.message || ''

          if (message.includes('article__featured_limit_reached')) {
            this.setFeedback({ message: this.$t('articles.feedback:featured.limit') })
          } else {
            this.setFeedback({ message: this.$t('global.error') })
          }

          this.featuredVersion += 1
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    publish (idArticle) {
      this.setFetching(true)

      this.attemptPublishArticle(idArticle)
        .then(() => {
          this.setFeedback({ message: this.$t('articles.feedback:publish.success') })
          this.getArticleList()
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('global.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    hide (idArticle) {
      this.setFetching(true)

      this.attemptHideArticle(idArticle)
        .then(() => {
          this.setFeedback({ message: this.$t('articles.feedback:hide.success') })
          this.getArticleList()
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('global.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    confirmRemove (idArticle) {
      this.articleId = idArticle
      this.modalRemoveOpen = true
    },

    cancelRemove () {
      this.modalRemoveOpen = false
    },

    remove () {
      this.modalRemoveOpen = false
      this.setFetching(true)

      this.attemptRemoveArticle(this.articleId)
        .then(() => {
          this.setFeedback({ message: this.$t('articles.feedback:remove.success') })

          if (this.articlesList.length === 1 && this.pagination.page > 1) {
            this.pagination.page -= 1
          } else {
            this.getArticleList()
          }
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('articles.feedback:remove.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    statusLabel (status) {
      return this.$t(`articles.status:${status}`)
    },

    statusClass (status) {
      const map = {
        published: 'active-ribbon',
        hidden: 'status-hidden',
        draft: 'pending-ribbon'
      }

      return map[status] || ''
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('articles.list:header.title')"
      :description="$t('articles.list:header.description')"
      background="/assets/images/themes/default/png/articles_banner.jpg"
    />
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('articles.list:btn.add')"
        @click="createNewArticle"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        @search="searchArticle"
      />
    </FilterList>
    <div
      v-if="articlesList.length > 0"
      class="center articles-table"
    >
      <Datatable :items="articlesList">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('articles.list:column.title') }}
            </th>
            <th
              class="th"
              width="180"
            >
              {{ $t('articles.list:column.category') }}
            </th>
            <th
              class="th"
              width="100"
            >
              {{ $t('articles.list:column.featured') }}
            </th>
            <th
              class="th"
              width="140"
            >
              {{ $t('articles.list:column.status') }}
            </th>
            <th class="th th-actions">
              {{ $t('articles.list:column.actions') }}
            </th>
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text td-block article-title">{{ props.item.title }}</span>
              <span
                v-if="props.item.publishedAt"
                class="td-text td-block article-meta"
              >{{ formatDate(props.item.publishedAt) }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ props.item.theme ? props.item.theme.name : '-' }}</span>
            </td>
            <td class="td">
              <CheckboxItem
                :key="`${props.item.id}-${props.item.featured}-${featuredVersion}`"
                :value="props.item.title"
                :checked="props.item.featured"
                :index="props.item.id"
                :disabled="!canWrite"
                @change="toggleFeatured(props.item)"
              />
            </td>
            <td class="td">
              <span
                class="td-tag"
                :class="statusClass(props.item.status)"
              >
                {{ statusLabel(props.item.status) }}
              </span>
            </td>
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  v-if="canWrite && props.item.status !== 'published'"
                  :text="$t('articles.action:publish')"
                  @click="publish(props.item.id)"
                />
                <DropdownLink
                  v-if="canWrite && props.item.status === 'published'"
                  :text="$t('articles.action:hide')"
                  @click="hide(props.item.id)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('articles.action:remove')"
                  @click="confirmRemove(props.item.id)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center articles-table"
    >
      <p>Nenhum artigo encontrado</p>
    </div>
    <ModalConfirm
      :active="modalRemoveOpen"
      :title="$t('articles.modal:remove.title')"
      :description="$t('articles.modal:remove.description')"
      @confirmAction="remove"
      @cancelAction="cancelRemove"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/articles.css"></style>

<style src="@/assets/styles/themes/default/checkbox.css"></style>
